import { useRef } from 'react';
import { X } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';
import { MOIS, TRIMESTRES } from '../../utils/constants';
import { useAjouterFacture } from '../../hooks/useAjouterFacture';
import ModalPortal from '../ModalPortal';

/**
 * AjouterFactureModal - Modale d'ajout de facture externe ou interne
 */
function AjouterFactureModal({ isOpen, onClose, onSubmit, type, prestataires, consultants }) {
  const mouseDownOnOverlay = useRef(false);

  const {
    formData,
    handleChange,
    handleSubmit,
    loadingNumero,
    lines,
    handleAddLine,
    handleRemoveLine,
    handleLineChange,
    montantHT,
    tauxTVA,
    montantTVA,
    montantTTC
  } = useAjouterFacture(isOpen, onSubmit, type);

  if (!isOpen) return null;

  const isExternal = type === 'externe';

  return (
    <ModalPortal>
      <style>{`
        .modal-scroll::-webkit-scrollbar { width: 6px; }
        .modal-scroll::-webkit-scrollbar-track {
          background: ${colors.background.light};
          border-radius: 3px;
          margin: 0.5rem 0;
        }
        .modal-scroll::-webkit-scrollbar-thumb {
          background: ${colors.secondary.lightTeal};
          border-radius: 3px;
        }
        .modal-scroll::-webkit-scrollbar-thumb:hover { background: ${colors.primary.teal}; }
        .modal-scroll { scrollbar-width: thin; scrollbar-color: ${colors.secondary.lightTeal} ${colors.background.light}; }
        .modal-input:focus { border-color: ${colors.primary.teal} !important; }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onMouseDown={(e) => { mouseDownOnOverlay.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && mouseDownOnOverlay.current) onClose(); }}
      >
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '0.75rem',
            width: '90%',
            maxWidth: '540px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh'
          }}
        >
          {/* Header */}
          <div style={{
            backgroundColor: colors.primary.teal,
            padding: '1rem 1.5rem',
            borderTopLeftRadius: '0.75rem',
            borderTopRightRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: colors.text.white,
              fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
            }}>
              Ajouter une facture {isExternal ? 'externe' : 'interne'}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none',
                cursor: 'pointer', padding: '0.25rem',
                display: 'flex', alignItems: 'center',
                color: colors.text.white
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Contenu scrollable */}
          <div className="modal-scroll" style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
            <form onSubmit={handleSubmit}>

              {/* === Tiers === */}
              {isExternal ? (
                <FormField label="Prestataire" required>
                  <select
                    name="prestataire"
                    value={formData.prestataire || ''}
                    onChange={handleChange}
                    required
                    className="modal-input"
                    style={inputStyle}
                  >
                    <option value="">Sélectionner un prestataire</option>
                    {prestataires?.map(p => (
                      <option key={p.id} value={p.id}>{p.fields?.Nom || 'Sans nom'}</option>
                    ))}
                  </select>
                </FormField>
              ) : (
                <FormField label="Consultant" required>
                  <select
                    name="consultant"
                    value={formData.consultant || ''}
                    onChange={handleChange}
                    required
                    className="modal-input"
                    style={inputStyle}
                  >
                    <option value="">Sélectionner un consultant</option>
                    {consultants?.map(c => (
                      <option key={c.id} value={c.id}>{c.fields?.Nom || 'Sans nom'}</option>
                    ))}
                  </select>
                </FormField>
              )}

              {/* Numéro de facture */}
              <FormField label="Numéro de facture" required>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={formData.numeroFacture}
                    readOnly
                    required
                    placeholder={
                      isExternal
                        ? (!formData.prestataire ? 'Sélectionnez d\'abord un prestataire' : loadingNumero ? 'Calcul en cours...' : '')
                        : (!formData.consultant  ? 'Sélectionnez d\'abord un consultant'  : loadingNumero ? 'Calcul en cours...' : '')
                    }
                    style={{
                      ...inputStyle,
                      backgroundColor: colors.background.light,
                      color: formData.numeroFacture ? colors.text.dark : colors.text.secondary,
                      cursor: 'default',
                      paddingRight: loadingNumero ? '2.5rem' : undefined
                    }}
                  />
                  {loadingNumero && (
                    <div style={{
                      position: 'absolute', right: '0.75rem', top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px', height: '16px',
                      border: `2px solid ${colors.secondary.lightTeal}`,
                      borderTop: `2px solid ${colors.primary.teal}`,
                      borderRadius: '50%',
                      animation: 'spinNumero 0.8s linear infinite'
                    }} />
                  )}
                  <style>{`@keyframes spinNumero { to { transform: translateY(-50%) rotate(360deg); } }`}</style>
                </div>
              </FormField>

              {/* === Période === */}
              {/* La date d'émission n'est plus saisie ici : elle est demandée au moment de
                  l'envoi (facture externe) ou du téléchargement (facture interne), seule
                  étape qui fixe réellement cette date. */}
              {isExternal ? (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr', gap: '0.75rem', marginBottom: '1rem' }}>
                  <FormField label="Mois" required noMargin>
                    <select name="mois" value={formData.mois || ''} onChange={handleChange} required className="modal-input" style={inputStyle}>
                      <option value="">— Mois —</option>
                      {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Année" required noMargin>
                    <input type="number" name="anneeExterne" value={formData.anneeExterne} onChange={handleChange} required min="2020" max="2099" className="modal-input" style={inputStyle} />
                  </FormField>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr', gap: '0.75rem', marginBottom: '1rem' }}>
                  <FormField label="Trimestre" required noMargin>
                    <select name="trimestre" value={formData.trimestre || ''} onChange={handleChange} required className="modal-input" style={inputStyle}>
                      <option value="">— Trim. —</option>
                      {TRIMESTRES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Année" required noMargin>
                    <input type="number" name="anneeInterne" value={formData.anneeInterne} onChange={handleChange} required min="2020" max="2099" className="modal-input" style={inputStyle} />
                  </FormField>
                </div>
              )}

              {/* === Destinataire (internes uniquement) === */}
              {!isExternal && (
                <>
                  <FormField label="Destinataire" required>
                    <select name="destinataireType" value={formData.destinataireType} onChange={handleChange} className="modal-input" style={inputStyle}>
                      <option value="ephyco">EPHYCO</option>
                      <option value="prestataire">Autre prestataire</option>
                    </select>
                  </FormField>
                  {formData.destinataireType === 'prestataire' && (
                    <FormField label="Prestataire" required>
                      <select
                        name="destinataire"
                        value={formData.destinataire || ''}
                        onChange={handleChange}
                        required
                        className="modal-input"
                        style={inputStyle}
                      >
                        <option value="">Sélectionner un prestataire</option>
                        {prestataires?.map(p => (
                          <option key={p.id} value={p.id}>{p.fields?.Nom || 'Sans nom'}</option>
                        ))}
                      </select>
                    </FormField>
                  )}
                </>
              )}

              {/* Intitulé */}
              <FormField label="Intitulé">
                <input
                  type="text"
                  name="intitule"
                  value={formData.intitule}
                  onChange={handleChange}
                  placeholder="Description générale (optionnel)"
                  className="modal-input"
                  style={inputStyle}
                />
              </FormField>

              {/* Lignes de détail */}
              <LinesEditor
                lines={lines}
                onAddLine={handleAddLine}
                onRemoveLine={handleRemoveLine}
                onLineChange={handleLineChange}
              />

              {/* Taux de TVA */}
              <FormField label="TVA (%)">
                <select name="tauxTVA" value={formData.tauxTVA} onChange={handleChange} className="modal-input" style={inputStyle}>
                  <option value="0">0%</option>
                  <option value="5.5">5,5%</option>
                  <option value="10">10%</option>
                  <option value="20">20%</option>
                </select>
              </FormField>

              {/* Récapitulatif */}
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: colors.secondary.lightGray,
                borderRadius: '0.5rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  <span style={{ color: colors.primary.gray }}>Montant HT :</span>
                  <span style={{ color: colors.primary.gray, fontWeight: '600' }}>{formatEuro(montantHT)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  <span style={{ color: colors.primary.gray }}>TVA ({tauxTVA}%) :</span>
                  <span style={{ color: colors.primary.gray, fontWeight: '600' }}>{formatEuro(montantTVA)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: `1px solid ${colors.secondary.mediumGray}` }}>
                  <span style={{ fontWeight: 'bold', color: colors.primary.gray }}>Montant TTC :</span>
                  <span style={{ color: colors.primary.teal, fontWeight: 'bold', fontSize: '1.125rem' }}>{formatEuro(montantTTC)}</span>
                </div>
              </div>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={cancelBtnStyle}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={submitBtnStyle}
                >
                  Créer la facture
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// === STYLES ===

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: `2px solid ${colors.secondary.lightTeal}`,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: '"Noto Sans", sans-serif',
  backgroundColor: colors.text.white
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '600',
  color: colors.primary.gray,
  fontSize: '0.9375rem'
};

const cancelBtnStyle = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '0.5rem',
  backgroundColor: colors.secondary.mediumGray,
  color: colors.text.white,
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  fontFamily: '"Noto Sans", sans-serif'
};

const submitBtnStyle = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '0.5rem',
  backgroundColor: colors.primary.teal,
  color: colors.text.white,
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  fontFamily: '"Noto Sans", sans-serif'
};

// === SOUS-COMPOSANTS ===

function FormField({ label, required, noMargin, children }) {
  return (
    <div style={{ marginBottom: noMargin ? 0 : '1rem' }}>
      <label style={labelStyle}>
        {label}{required && ' *'}
      </label>
      {children}
    </div>
  );
}

function LinesEditor({ lines, onAddLine, onRemoveLine, onLineChange }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={labelStyle}>Lignes de détail</label>
      {lines.map((line, index) => (
        <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            value={line.description}
            onChange={(e) => onLineChange(index, 'description', e.target.value)}
            placeholder="Description"
            required
            className="modal-input"
            style={{ ...inputStyle, flex: 3 }}
          />
          <input
            type="number"
            value={line.montant}
            onChange={(e) => onLineChange(index, 'montant', e.target.value)}
            placeholder="Montant €"
            required
            min="0.01"
            step="0.01"
            className="modal-input"
            style={{ ...inputStyle, flex: 1 }}
          />
          {lines.length > 1 && (
            <button
              type="button"
              onClick={() => onRemoveLine(index)}
              style={{
                background: 'none', border: 'none',
                cursor: 'pointer', fontSize: '1.25rem',
                color: colors.text.secondary,
                padding: '0 0.25rem', lineHeight: 1
              }}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={onAddLine}
        style={{
          background: 'none',
          border: `1px dashed ${colors.secondary.lightTeal}`,
          borderRadius: '0.5rem',
          padding: '0.4rem 0.75rem',
          cursor: 'pointer',
          color: colors.primary.teal,
          fontSize: '0.875rem',
          fontWeight: '500',
          fontFamily: '"Noto Sans", sans-serif'
        }}
      >
        + Ajouter une ligne
      </button>
    </div>
  );
}

export default AjouterFactureModal;
